import { useEffect, useState } from 'react'
import _ from 'lodash'
import { Grid } from '@design-systems/canvas-ui'
import {
  renderControlLabel,
  showErrorMessage,
  renderControlLabelSmallHeaderOnly,
} from '../Builders'
import { buildDropdownOptions } from '../../common/helper'
import {
  getProductType,
  getProductSubtypes,
  getMerchTypes,
  getItemTypes,
  getProductGroup,
} from '../../common/helper'
import { getControlError } from '../../common/helper'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import {
  CLIENT_TRIM_PRODUCTS,
  RFI_TYPE_LOCATION,
} from '../../common/commonConstants'

const CLIENT_LEAD_TIME = 'lead_time'
const CLIENT_TOTAL_PRODUCT_CAPABILITIES = 'total_product_capabilities'
const CLIENT_FACTORY_CAPABILITIES = 'factory_capabilities'
const PROP_PRODUCT_CAPABILITY = '.product_capability'
const PROP_FACTORY_CAPABILITY = '.factory_capability'
const LABEL_PROD_TYPE = 'Product Type'
const LABEL_PROD_SUB_TYPE = 'Product Sub-Type'
const LABEL_MERCH_TYPE = 'Merch type'
const LABEL_ITEM_TYPE = 'Item type'

const columnWidths = {
  default: {
    product_type: 3,
    product_sub_type: 3,
    merchandise_type: 3,
    item_type: 3,
  },
  trim_products: {
    product_type: 4,
    product_sub_type: 4,
    item_type: 4,
  },
}

const ProductHierarchySelector = (props) => {
  const CLIENT_PROPERTY =
    props.rfiType === RFI_TYPE_LOCATION
      ? CLIENT_FACTORY_CAPABILITIES
      : CLIENT_TOTAL_PRODUCT_CAPABILITIES
  const CLIENT_SUB_PROPERTY =
    props.rfiType === RFI_TYPE_LOCATION
      ? PROP_FACTORY_CAPABILITY
      : PROP_PRODUCT_CAPABILITY
  const [capabilitiesData, setCapabilitiesData] = useState(null)
  const [isMerchTypeUpdated, setIsMerchTypeUpdated] = useState(false)

  useEffect(() => {
    setCapabilitiesData(props.capabilitiesData)
  }, [props.capabilitiesData])

  useEffect(() => {
    if (
      capabilitiesData &&
      !_.isEqual(props.capabilitiesData, capabilitiesData)
    ) {
      props.updateTPCHierarchy(
        props.tPCIndex,
        props.subSectionReferenceKey,
        capabilitiesData,
      )
      if (isMerchTypeUpdated && props.clientType === CLIENT_PROPERTY) {
        props.setIsSaveAndRefresh(true)
        setIsMerchTypeUpdated(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilitiesData, isMerchTypeUpdated])

  /**
   * Get hierarchy column widths
   * @param {*} property
   * @returns
   */
  const getColumnWidth = (property) => {
    let client = props.clientType?.toLowerCase()
    return client === CLIENT_TRIM_PRODUCTS
      ? columnWidths[client][property]
      : columnWidths['default'][property]
  }

  /**
   * Update product type
   * @param {*} newValue
   */
  const updateProductType = (newValue) => {
    const hadMerchSelected = !!capabilitiesData?.merchandise_type
    const nextProductType = newValue?.value ? newValue.value : null
    const isClearingProductType = !nextProductType
    setCapabilitiesData({
      ...capabilitiesData,
      item_type: props.clientType === CLIENT_LEAD_TIME ? '' : [],
      merchandise_type: null,
      product_sub_type: null,
      product_type: newValue?.value ? newValue.value : null,
      group_names: [],
    })
    if (hadMerchSelected && isClearingProductType) {
      setIsMerchTypeUpdated(true)
    }
  }

  /**
   * Update product sub-type
   * @param {*} newValue
   */
  const updateProductSubType = (newValue) => {
    const hadMerchSelected = !!capabilitiesData?.merchandise_type
    const nextSubType = newValue?.value ? newValue.value : null
    const isClearingSubType = !nextSubType
    setCapabilitiesData({
      ...capabilitiesData,
      item_type: props.clientType === CLIENT_LEAD_TIME ? '' : [],
      merchandise_type: null,
      product_sub_type: newValue?.value ? newValue.value : null,
      group_names: [],
    })
    if (hadMerchSelected && isClearingSubType) {
      setIsMerchTypeUpdated(true)
    }
  }

  /**
   * Update merch type
   * @param {*} newValue
   */
  const updateMerchType = (newValue) => {
    let newEvaluatedGroup = []

    if (!newValue) {
      setCapabilitiesData({
        ...capabilitiesData,
        item_type: props.clientType === CLIENT_LEAD_TIME ? '' : [],
        merchandise_type: null,
        group_names: [],
      })
    } else {
      //2. Find the new group by merch selection
      newEvaluatedGroup = getProductGroup(
        props.rfiMasterData?.common?.product_types,
        capabilitiesData.product_type,
        capabilitiesData.product_sub_type,
        newValue?.value,
      )
      setCapabilitiesData({
        ...capabilitiesData,
        item_type: props.clientType === CLIENT_LEAD_TIME ? '' : [],
        merchandise_type: newValue?.value,
        group_names: newEvaluatedGroup,
      })
    }

    //Set update from merch type -> should trigger save + refresh
    setIsMerchTypeUpdated(true)
  }

  /**
   * Update item type
   * @param {*} newValue
   */
  const updateItemType = (newValue) => {
    let newItemTypes = null
    if (
      props.clientType?.toLowerCase() === CLIENT_PROPERTY ||
      props.clientType?.toLowerCase() === CLIENT_TRIM_PRODUCTS
    ) {
      newItemTypes = newValue.map((eachValue) => {
        return eachValue.value
      })
    } else if (props.clientType?.toLowerCase() === CLIENT_LEAD_TIME) {
      newItemTypes = newValue?.value
    }
    setCapabilitiesData({
      ...capabilitiesData,
      item_type: newItemTypes,
    })
  }

  /**
   * Get dropdown options for ProductType / SubType / Merchtype / Item type
   * @param {*} propertyId
   * @returns
   */
  const getDropdownOptions = (propertyId) => {
    let dropdownOptions = []
    switch (propertyId.toLowerCase()) {
      case 'product_type':
        dropdownOptions = buildDropdownOptions(
          getProductType(props.rfiMasterData?.common?.product_types),
          null,
          true,
          false,
        )
        break
      case 'product_sub_type':
        dropdownOptions = buildDropdownOptions(
          getProductSubtypes(
            props.rfiMasterData?.common?.product_types,
            capabilitiesData.product_type,
          ),
          null,
          true,
          false,
        )
        break
      case 'merchandise_type':
        dropdownOptions = buildDropdownOptions(
          getMerchTypes(
            props.rfiMasterData?.common?.product_types,
            capabilitiesData.product_type,
            capabilitiesData.product_sub_type,
          ),
          null,
          true,
          false,
        )
        break
      case 'item_type':
        if (props.clientType === CLIENT_TRIM_PRODUCTS) {
          dropdownOptions = buildDropdownOptions(
            getItemTypes(
              props.rfiMasterData?.common?.product_types,
              capabilitiesData.product_type,
              capabilitiesData.product_sub_type,
              null,
            ),
            null,
            true,
            false,
          )
        } else {
          dropdownOptions = buildDropdownOptions(
            getItemTypes(
              props.rfiMasterData?.common?.product_types,
              capabilitiesData.product_type,
              capabilitiesData.product_sub_type,
              capabilitiesData.merchandise_type,
            ),
            null,
            true,
            false,
          )
        }
        break
      default:
        break
    }
    return dropdownOptions
  }

  /**
   * Render product hierarchy selector
   * @returns
   */
  const generateProductHierarchySelector = () => {
    let ptError = getControlError(
      `${props.sectionTemplatePath}${
        props.clientType === CLIENT_PROPERTY ? CLIENT_SUB_PROPERTY : ''
      }.product_type`,
      props.errorCache,
    )
    let pstError = getControlError(
      `${props.sectionTemplatePath}${
        props.clientType === CLIENT_PROPERTY ? CLIENT_SUB_PROPERTY : ''
      }.product_sub_type`,
      props.errorCache,
    )
    let mtError = getControlError(
      `${props.sectionTemplatePath}${
        props.clientType === CLIENT_PROPERTY ? CLIENT_SUB_PROPERTY : ''
      }.merchandise_type`,
      props.errorCache,
    )
    let iError = getControlError(
      `${props.sectionTemplatePath}${
        props.clientType === CLIENT_PROPERTY ? CLIENT_SUB_PROPERTY : ''
      }.item_type`,
      props.errorCache,
    )

    return (
      <>
        {/* 1. Product Type */}
        <Grid.Item size={{ xs: getColumnWidth('product_type') }}>
          {props.isReadonly
            ? renderControlLabelSmallHeaderOnly({ label_name: LABEL_PROD_TYPE })
            : renderControlLabel({
                label_name: LABEL_PROD_TYPE,
                is_mandatory: true,
              })}
          <AutocompleteInput
            name={`ac-product-type-${props.tPCIndex}`}
            error={ptError}
            readOnly={props.isReadonly}
            value={
              capabilitiesData.product_type ? capabilitiesData.product_type : ''
            }
            class="font-size-16"
            options={getDropdownOptions('product_type')}
            onUpdate={(value) => updateProductType(value)}
          />
          <div className="hc8-pt-2x">{showErrorMessage(ptError)}</div>
        </Grid.Item>

        {/* 2. Product Sub-Type */}
        <Grid.Item size={{ xs: getColumnWidth('product_sub_type') }}>
          {props.isReadonly
            ? renderControlLabelSmallHeaderOnly({
                label_name: LABEL_PROD_SUB_TYPE,
              })
            : renderControlLabel({
                label_name: LABEL_PROD_SUB_TYPE,
                is_mandatory: true,
              })}
          <AutocompleteInput
            name={`ac-product-sub-type-${props.tPCIndex}`}
            value={
              capabilitiesData.product_sub_type
                ? capabilitiesData.product_sub_type
                : ''
            }
            error={pstError}
            readOnly={props.isReadonly}
            disabled={!capabilitiesData.product_type}
            class="font-size-16"
            options={getDropdownOptions('product_sub_type')}
            onUpdate={(value) => updateProductSubType(value)}
          />
          <div className="hc8-pt-2x">{showErrorMessage(pstError)}</div>
        </Grid.Item>

        {/* 3. Merch Type */}
        {props.clientType !== CLIENT_TRIM_PRODUCTS && (
          <Grid.Item size={{ xs: getColumnWidth('merchandise_type') }}>
            {props.isReadonly
              ? renderControlLabelSmallHeaderOnly({
                  label_name: LABEL_MERCH_TYPE,
                })
              : renderControlLabel({
                  label_name: LABEL_MERCH_TYPE,
                  is_mandatory: props.clientType !== CLIENT_LEAD_TIME,
                })}
            <AutocompleteInput
              name={`ac-merch-type-${props.tPCIndex}`}
              value={
                capabilitiesData.merchandise_type
                  ? capabilitiesData.merchandise_type
                  : ''
              }
              readOnly={props.isReadonly}
              error={mtError}
              disabled={!capabilitiesData.product_sub_type}
              class="font-size-16"
              options={getDropdownOptions('merchandise_type')}
              onUpdate={(value) => updateMerchType(value)}
            />
            <div className="hc8-pt-2x">{showErrorMessage(mtError)}</div>
          </Grid.Item>
        )}

        {/* 4. Item Type */}
        <Grid.Item size={{ xs: getColumnWidth('item_type') }}>
          {props.isReadonly
            ? renderControlLabelSmallHeaderOnly({ label_name: LABEL_ITEM_TYPE })
            : renderControlLabel({ label_name: LABEL_ITEM_TYPE })}
          {(props.clientType?.toLowerCase() === CLIENT_PROPERTY ||
            props.clientType?.toLowerCase() === CLIENT_TRIM_PRODUCTS) && (
            <>
              <AutocompleteInput
                name={`acm-item-type-${props.tPCIndex}`}
                value={buildDropdownOptions(
                  capabilitiesData.item_type,
                  null,
                  false,
                  false,
                )}
                readOnly={props.isReadonly}
                error={iError}
                disabled={
                  props.clientType?.toLowerCase() === CLIENT_TRIM_PRODUCTS
                    ? !capabilitiesData.product_sub_type
                    : !capabilitiesData.merchandise_type
                }
                multiselect={true}
                defaultPlaceholder={'Please select option(s)'}
                class="font-size-16"
                options={getDropdownOptions('item_type')}
                onUpdate={(value) => updateItemType(value)}
              />
              <div className="hc8-pt-2x">{showErrorMessage(iError)}</div>
            </>
          )}
          {props.clientType?.toLowerCase() === CLIENT_LEAD_TIME && (
            <>
              <AutocompleteInput
                name={`ac-item-type-${props.tPCIndex}`}
                value={
                  capabilitiesData.item_type ? capabilitiesData.item_type : ''
                }
                readOnly={props.isReadonly}
                error={iError}
                errorText={iError}
                disabled={!capabilitiesData.merchandise_type}
                class="font-size-16"
                options={getDropdownOptions('item_type')}
                onUpdate={(value) => updateItemType(value)}
              />
              <div className="hc8-pt-2x">{showErrorMessage(iError)}</div>
            </>
          )}
        </Grid.Item>
      </>
    )
  }

  return (
    <Grid.Container>
      {capabilitiesData && generateProductHierarchySelector()}
    </Grid.Container>
  )
}

export default ProductHierarchySelector
